import { useMemo } from 'react'

import { matchFilterNumber, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'
import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'
import type { UseLoadMorePayload } from '@domain-group/react-hooks'

import useSelector from '@hooks/useSelector'
import useReduxAction from '@hooks/useReduxAction'

import type { EntityOptions } from '@redux/modules/types'

import type { PaginationRuleModel } from '@models/types'

const watchEntityKeys = ['paginationRules']

type PaginationRulesFilters = {
  brand_id?: number,
  string?: string,
}

type Options = {
  entityKey?: string,
  filters?: PaginationRulesFilters,
  performHttpRequests?: boolean,
  requestOptions?: EntityOptions,
}

interface UsePaginationRulesPayload {
  callbacks: {
    loadMore: () => void,
  },
  canLoadMore: boolean,
  filteredPaginationRules: PaginationRuleModel[],
  filteredPaginationRulesCount: number,
  hasFilteredPaginationRules: boolean,
  loading: boolean,
}

function usePaginationRules(options: Options = {}): UsePaginationRulesPayload {
  const { entityKey, filters, requestOptions } = options

  const { brand_id: filterBrandId, string: filterString } = filters || {}

  const {
    updatedEntities: { paginationRules: paginationRulesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const paginationRules = useSelector(reduxState => reduxState.entities.paginationRules)

  const filteredPaginationRules = useMemo((): PaginationRuleModel[] => {
    const filtered = Object.values(paginationRules).filter((paginationRule) => {
      const { brand_id, id, title } = paginationRule

      const matchBrand: boolean = matchFilterNumber(brand_id, filterBrandId)
      const matchTitle: boolean = matchFilterString(title, filterString)
      const matchId: boolean = id?.toString() === filterString
      const matchString: boolean = matchId || matchTitle

      return matchBrand && matchString
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [paginationRulesUpdatedAt, JSON.stringify(filters)])

  const filteredPaginationRulesCount: number = filteredPaginationRules.length

  const loadMorePayload: UseLoadMorePayload = useLoadMore({
    loadedCount: filteredPaginationRulesCount,
    filters,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingPaginationRules } = useReduxAction(
    'paginationRules',
    'loadPaginationRules',
    {
      entityKey,
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filters, performHttpRequests],
    {
      // @ts-expect-error TS2339
      shouldPerformFn: ({ loading }) => !!performHttpRequests && !loading,
    },
  )

  const hasFilteredPaginationRules = !!filteredPaginationRules.length

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredPaginationRules,
    filteredPaginationRulesCount,
    hasFilteredPaginationRules,
    loading: !!loadingPaginationRules,
  }
}

export default usePaginationRules
